import {
  Ship,
  Globe2,
  FileText,
  Tags,
  Radar,
  Package,
  Plane,
  ShieldCheck,
  Languages,
  Home,
  ArrowUpRight,
} from "lucide-react";

const SERVICES_MACRO =
  "https://images.unsplash.com/photo-1642418934307-cc2c5bf83e65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMGNvbnRhaW5lciUyMGFic3RyYWN0fGVufDB8fHx8MTc3ODAzOTEyOHww&ixlib=rb-4.1.0&q=85";

const cards = [
  {
    key: "exporting",
    title: "Customized Exporting",
    lead: "Door-to-door delivery, tailored to your cargo, your route, your timeline.",
    icon: Ship,
    span: "md:col-span-6 lg:col-span-5 md:row-span-2",
    tall: true,
  },
  {
    key: "turnkey",
    title: "Turnkey Furnishing & Building",
    lead: "Design, drawings, sourcing and delivery for hotels, offices and residences — from Yiwu, Foshan & Guangzhou.",
    icon: Home,
    span: "md:col-span-6 lg:col-span-4",
    accent: true,
  },
  {
    key: "logistics",
    title: "Global Logistics",
    lead: "A worldwide network, coordinated from Yiwu — from pickup to final mile.",
    icon: Globe2,
    span: "md:col-span-6 lg:col-span-3",
  },
  {
    key: "docs",
    title: "Expert Documentation",
    lead: "Customs paperwork handled precisely, so your goods move without friction.",
    icon: FileText,
    span: "md:col-span-6 lg:col-span-4",
  },
  {
    key: "pricing",
    title: "Competitive Pricing",
    lead: "Transparent, volume-smart pricing for businesses shipping at any scale.",
    icon: Tags,
    span: "md:col-span-6 lg:col-span-4",
  },
  {
    key: "tracking",
    title: "Real-time Tracking",
    lead: "Live visibility on every container, route and ETA — 24/7.",
    icon: Radar,
    span: "md:col-span-6 lg:col-span-4",
  },
  {
    key: "packaging",
    title: "Expert Packaging",
    lead: "Protective, regulation-ready packaging for every mode of transport.",
    icon: Package,
    span: "md:col-span-12 lg:col-span-4",
  },
  {
    key: "travel",
    title: "Travel Assistance",
    lead: "Visa letters, bookings and on-ground guidance for your China sourcing trip.",
    icon: Plane,
    span: "md:col-span-4",
  },
  {
    key: "customs",
    title: "Customs & Door Delivery",
    lead: "Clearance across India, UK, Middle East and beyond. You receive, we handle the rest.",
    icon: ShieldCheck,
    span: "md:col-span-4",
  },
  {
    key: "sourcing",
    title: "Multilingual Sourcing",
    lead: "We walk the Yiwu market with you — translating, negotiating, verifying.",
    icon: Languages,
    span: "md:col-span-4",
  },
];

function ServiceCard({ item }) {
  const Icon = item.icon;
  return (
    <article
      data-testid={`service-card-${item.key}`}
      className={`group relative overflow-hidden bg-white border border-black/5 p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(16,24,40,0.25)] ${item.span || "md:col-span-4"}`}
    >
      {item.tall && (
        <>
          <img
            src={SERVICES_MACRO}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-80 transition-opacity duration-500"
          />
          <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/70 transition-colors duration-500" />
        </>
      )}

      <div className="relative flex flex-col h-full min-h-[240px]">
        <div className="flex items-center justify-between">
          <div
            className={`w-12 h-12 flex items-center justify-center border ${
              item.tall
                ? "border-ink-100 text-ink-900 group-hover:border-white/30 group-hover:text-white"
                : "border-ink-100 text-ink-900 group-hover:border-signal group-hover:text-signal"
            } transition-colors`}
          >
            <Icon className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <ArrowUpRight
            className={`w-5 h-5 transition-all duration-300 ${
              item.tall
                ? "text-ink-300 group-hover:text-white"
                : "text-ink-300 group-hover:text-signal"
            } group-hover:translate-x-1 group-hover:-translate-y-1`}
            strokeWidth={1.6}
          />
        </div>

        <h3
          className={`mt-auto font-heading font-bold tracking-tight text-2xl md:text-3xl ${
            item.tall ? "text-ink-900 group-hover:text-white" : "text-ink-900"
          } ${item.tall ? "md:text-4xl" : ""} transition-colors`}
        >
          {item.title}
        </h3>
        <p
          className={`mt-4 text-[15px] leading-relaxed max-w-md ${
            item.tall ? "text-ink-500 group-hover:text-white/80" : "text-ink-500"
          } transition-colors`}
        >
          {item.lead}
        </p>
      </div>
    </article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-ink-50 py-14 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-end mb-12 md:mb-16">
          <div className="md:col-span-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
              01 — What we do
            </p>
            <h2 className="font-heading font-black text-ink-900 tracking-[-0.03em] leading-[0.95] text-4xl md:text-5xl lg:text-6xl">
              Ten moving parts.<br />One operator.
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 text-ink-500 text-base md:text-lg leading-relaxed">
            From Yiwu wholesale floors to your warehouse door, Emporia orchestrates
            the entire exporting chain. Pick a service or let us design the full
            route — we've done it for fifteen years.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 auto-rows-[minmax(240px,auto)]">
          {cards.map((c) => (
            <ServiceCard key={c.key} item={c} />
          ))}
        </div>
      </div>
    </section>
  );
}
